import { z } from 'zod';

import { apiErrorReason } from './api';

// Capacidades efectivas del actor sobre UN documento (spec 2026-10-14 §8 D6): las calcula
// el servidor con las reglas del tipo. La ficha y el editor no ofrecen una acción que el
// servidor rechazaría y dicen por qué.

const capabilityStateSchema = z.union([
  z.object({ allowed: z.literal(true) }),
  z.object({
    allowed: z.literal(false),
    // Imprimir también según el estado del documento (sin versión aprobada, o no vigente
    // con "solo vigentes"): el servidor rechazaría la copia.
    reason: z.enum(['type_restricted', 'missing_permission', 'no_official_version', 'not_current']),
  }),
]);
export type CapabilityState = z.infer<typeof capabilityStateSchema>;

export const documentCapabilitiesSchema = z.object({
  edit_online: capabilityStateSchema,
  print: capabilityStateSchema,
  comment: capabilityStateSchema,
});
export type DocumentCapabilities = z.infer<typeof documentCapabilitiesSchema>;

/** Verbo de la acción en infinitivo, para las notas ("comentar", "imprimir"). */
export type ActionVerb = 'editar en línea' | 'imprimir' | 'comentar';

/** Por qué no está la acción, o null si está permitida. */
export function capabilityNote(state: CapabilityState, verb: ActionVerb): string | null {
  if (state.allowed) {
    return null;
  }
  switch (state.reason) {
    case 'type_restricted':
      return `Este tipo de documento no permite ${verb} a tu rol.`;
    case 'no_official_version':
      return NO_OFFICIAL_VERSION_TEXT;
    case 'not_current':
      return NOT_CURRENT_TEXT;
    default:
      return `Tu rol no permite ${verb}.`;
  }
}

/** Copias: el documento nunca se aprobó, no hay PDF que copiar. */
export const NO_OFFICIAL_VERSION_TEXT =
  'Este documento todavía no tiene una versión aprobada: las copias estarán disponibles al aprobarse.';
/** Copias con la opción "solo vigentes" sobre un documento que no lo está. */
export const NOT_CURRENT_TEXT = 'Solo se pueden imprimir o descargar documentos vigentes.';

/** El servidor rechazó la acción porque una regla del tipo se la quita al rol. */
export function isTypeRestricted(err: unknown): boolean {
  return apiErrorReason(err) === 'document_type_restricted';
}

export function restrictedActionText(verb: ActionVerb): string {
  return `Este tipo de documento no permite ${verb} a tu rol. Si lo necesitas, pide acceso al Administrador de Calidad.`;
}
